import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { DownloadButtonComponent } from '@foundation/files/ui';
import {
	DocumentLine,
	DocumentParty,
	FACTURX_MISSING_HEADER,
	IN_PROCESS_TARGET,
	Invoice,
	InvoiceConfig,
	ValidationReport,
	VatExemption,
} from '@foundation/invoices/models';
import { InvoicesRepository } from '@foundation/invoices/state';
import { NotificationService } from '@foundation/notification';
import { Receipt, ReceiptConfig } from '@foundation/receipts/models';
import { ReceiptsRepository } from '@foundation/receipts/state';
import { TranslateDirective, TranslationService } from '@foundation/translations/services';

import { BillingDetailsFormComponent } from '../billing-details-form/billing-details-form.component';
import { RequestResponse } from '@foundation/network/services';
import { debounceTime, finalize, Observable, switchMap } from 'rxjs';

/** The buyer as the panel holds it, before it is sent. */
type BuyerForm = Required<Pick<DocumentParty, 'name'>> & Record<string, string>;

/**
 * One document, opened in place inside the table that lists it: what it says, what can still be
 * done to it, and the form that fixes what a compliant invoice needs.
 *
 * Declared in the invoices lib and used for receipts too. The two families differ in their config
 * and their numbering, not in what an admin does with a draft, and a second copy of this would be
 * a second place for those actions to drift.
 *
 * It talks to the resource's own repository rather than being handed callbacks: every action here
 * is an operation on this document, the repository owns those, and what comes back goes into the
 * store, which is what updates the row behind this panel.
 */
@Component({
	selector: 'lib-document-draft-panel',
	standalone: true,
	imports: [CommonModule, FormsModule, TranslateDirective, DownloadButtonComponent, BillingDetailsFormComponent],
	templateUrl: './document-draft-panel.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentDraftPanelComponent {
	private _invoices = inject(InvoicesRepository);
	private _receipts = inject(ReceiptsRepository);
	private _notificationService = inject(NotificationService);
	private _translationService = inject(TranslationService);

	/** The document being written. It may not exist yet: a creation is this same editor over a
	 * proposal the server made, which is why nothing here reads the database. */
	document = input.required<Invoice | Receipt>();
	kind = input.required<'invoice' | 'receipt'>();
	/** Who it is for. Only asked for while creating -- an existing document already says. */
	beneficiaryId = input<string | null>(null);

	/** The row is gone and the list has to be told: a deletion moves every later row up a place. */
	deleted = output<Invoice | Receipt>();
	/** A document that did not exist now does, and the list has never heard of it. */
	created = output<Invoice | Receipt>();
	/** The number was really taken, so the prediction the page shows is stale. */
	issued = output<Invoice | Receipt>();

	protected readonly IN_PROCESS_TARGET = IN_PROCESS_TARGET;

	isBusy = signal(false);
	isChecking = signal(false);
	/** Off by default: our own rules cost nothing, while the outside engine spends a credit and
	 * hands the document to a third party. Asked for each time rather than remembered. */
	useExternalValidator = signal(false);
	report = signal<ValidationReport | null>(null);
	/** The rules the document failed, in the words of the rule. */
	complianceFailures = signal<string[]>([]);
	vatExemptions = signal<VatExemption[]>([]);

	/** What the form holds. Seeded from the document each time a different one is shown, so the
	 * panel can say whether anything is actually different rather than whether it was touched. */
	buyer = signal<BuyerForm>({ name: '' });
	vatExemption = signal('');
	/** The lines, editable here too: a draft exists to be corrected, and a wrong price is the most
	 * likely thing to be wrong about one. */
	lines = signal<DocumentLine[]>([]);
	/** Prefilled from what the customer asked for on their profile, and the admin's to change: it
	 * decides one printed line, not what is owed. */
	indicativeCurrency = signal<string | null>(null);

	private _stored = computed(() => {
		const config = this.document().config;
		return {
			// The party whole, never a list of its fields. Enumerating them here is how the VAT
			// number went missing: it was added to the form and not to this copy, so it was neither
			// shown nor kept -- and saving would have written the document's own value away.
			buyer: { name: '', ...(config.buyer ?? {}) } as BuyerForm,
			vatExemption: config.vatExemption ?? '',
			lines: config.lines ?? [],
			indicativeCurrency: config.indicativeCurrency ?? null,
		};
	});

	/** Field by field over both sides, with an absent value and an empty one meaning the same: the
	 * form writes '' where the document says nothing, and that is not a change worth saving. */
	private _sameParty(left: BuyerForm, right: BuyerForm) {
		const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
		return [...keys].every((key) => (left[key] ?? '') === (right[key] ?? ''));
	}

	/** What the panel would save, shaped as the patch it will be sent as. */
	private _edited = computed(() => ({
		config: {
			...this.document().config,
			buyer: { ...this.buyer() } as DocumentParty,
			vatExemption: this.vatExemption().trim() || null,
			lines: this.lines().filter((line) => line.label.trim()),
			indicativeCurrency: this.indicativeCurrency(),
		},
	}));

	/** Different, not merely touched: typing a value and typing it back leaves nothing to save. */
	hasChanges = computed(() => {
		const stored = this._stored();
		const buyer = this.buyer();
		return (
			this.vatExemption() !== stored.vatExemption ||
			this.indicativeCurrency() !== stored.indicativeCurrency ||
			!this._sameParty(buyer, stored.buyer) ||
			JSON.stringify(this.lines()) !== JSON.stringify(stored.lines)
		);
	});

	/** Whether the document as it stands would be refused. Issuing takes the number for good, so a
	 * button that spends one on a document our own rules already reject is a trap. */
	isRefused = computed(() => this.complianceFailures().length > 0);

	private _i18nSaved = this._translationService.prep('Saved on the draft.');
	private _i18nCreated = this._translationService.prep('Draft created.');
	private _i18nSaveFailed = this._translationService.prep('Could not save on the draft.');
	private _i18nDeleted = this._translationService.prep('Draft deleted.');
	private _i18nConfirmDeleteTitle = this._translationService.prep('Delete this draft?');
	private _i18nDeleteExplanation = this._translationService.prep('It has not been issued, so nothing has been allocated and no number will be skipped.');
	private _i18nDelete = this._translationService.prep('Delete it');
	private _i18nDeleteFailed = this._translationService.prep('Could not delete that draft.');
	private _i18nIssueFailed = this._translationService.prep('Could not issue that document.');
	private _i18nPreviewFailed = this._translationService.prep('Could not render that document.');
	private _i18nPreviewWithoutXml = this._translationService.prep('That preview printed without its Factur-X XML.');
	private _i18nCheckFailed = this._translationService.prep('Could not check that document.');
	private _i18nRedrawn = this._translationService.prep('The draft was redrawn onto the next free number — issue it again.');
	private _i18nIssueExplanation = this._translationService.prep('The number is taken for good, the document is frozen and the customer can see it. There is no undo.');
	private _i18nConfirmIssueTitle = this._translationService.prep('Issue this document?');
	private _i18nIssue = this._translationService.prep('Issue');

	constructor() {
		effect(() => this._resetForm());
		effect(() => this._loadVatExemptions());
		// Checked as it is edited, against our own rules only: they cost nothing, they are the same
		// rules that block issuing, and finding out at the moment of issue is finding out too late.
		toObservable(this._edited)
			.pipe(debounceTime(600), switchMap((edited) => this._check$({ external: false, edited })), takeUntilDestroyed())
			.subscribe((response) => this._readReport(response));
	}

	private _check$({ external, edited }: { external: boolean; edited: { config: unknown } }) {
		const document = { ...this.document(), ...edited };
		return this.kind() === 'invoice'
			? this._invoices.checkInvoice$(document as Partial<Invoice>, { external })
			: this._receipts.checkReceipt$(document as Partial<Receipt>, { external });
	}

	/** One place reads a verdict, so the automatic check and the button cannot disagree. */
	private _readReport(response: RequestResponse<ValidationReport>) {
		if (response.error || !response.result) {
			this.report.set(null);
			// The rules it failed, in the words of the rule: each line names what it wanted.
			this.complianceFailures.set(response.error?.description?.split('\n').filter((line) => line.trim()) ?? []);
			return;
		}
		this.report.set(response.result);
		this.complianceFailures.set([]);
	}

	/** The wordings a zero-rated document may carry. Legal text, so the server owns the list. */
	private _loadVatExemptions() {
		if (this.kind() !== 'invoice') return;
		this._invoices.vatExemptions$().subscribe((response) => this.vatExemptions.set(response.result ?? []));
	}

	private _resetForm() {
		const stored = this._stored();
		this.buyer.set({ ...stored.buyer });
		this.vatExemption.set(stored.vatExemption);
		this.lines.set(stored.lines.map((line) => ({ ...line })));
		this.indicativeCurrency.set(stored.indicativeCurrency);
	}

	addLine() {
		this.lines.update((lines) => [...lines, { label: '', quantity: 1, unitPriceHt: 0, vatRate: 20 }]);
	}

	removeLine(index: number) {
		this.lines.update((lines) => lines.filter((_line, position) => position !== index));
	}

	updateLine(index: number, field: keyof DocumentLine, value: string | number) {
		this.lines.update((lines) => lines.map((line, position) => (position === index ? { ...line, [field]: value } : line)));
	}

	/** BR-27 forbids a negative line price outright, so the form refuses one rather than letting
	 * the schematron say it in French at issue time. */
	hasNegativeLine = computed(() => this.lines().some((line) => line.unitPriceHt < 0));
	totalTtc = computed(() =>
		Math.round(this.lines().reduce((sum, line) => sum + line.quantity * line.unitPriceHt * (1 + line.vatRate / 100), 0) * 100) / 100
	);

	setBuyerField(field: string, value: string) {
		this.buyer.update((buyer) => ({ ...buyer, [field]: value }));
	}

	/** The buyer as the shared form wants it, and back again. */
	buyerParty = computed(() => this.buyer() as DocumentParty);

	onBuyerChange(party: DocumentParty) {
		this.buyer.set({ ...(party as BuyerForm) });
	}

	private _i18nBuyerExplanation = this._translationService.prep(
		'A compliant invoice needs the customer’s address, their country (it decides the VAT treatment) and their email. A business in the EU needs its VAT number; a French one also needs its SIREN and SIRET, which is what routes the invoice to it there. These are written onto this draft only — the customer’s account is not changed.'
	);
	buyerExplanation = computed(() => this._i18nBuyerExplanation());

	/** Whether this document has been written down yet. A creation is the same editor, the same
	 * actions and the same preview over a document that has no row behind it. */
	isNew = computed(() => !this.document().timeCreated);

	save() {
		const document = this.document();
		const edited = this._edited();
		this.isBusy.set(true);
		const patched: Observable<RequestResponse<Invoice | Receipt>> = this.isNew() ? this._create$(document, edited) : this._patch$(document, edited);
		patched.pipe(finalize(() => this.isBusy.set(false))).subscribe({
			next: (response) => {
				if (response.error || !response.result) {
					// Silence here reads as "saved", and the next check would blame the document
					// rather than the save that never landed.
					this._notificationService.snackError(this._reasonOr(response.error, this._i18nSaveFailed()));
					return;
				}
				this._notificationService.snackSuccess(this.isNew() ? this._i18nCreated() : this._i18nSaved());
				if (this.isNew()) this.created.emit(response.result);
			},
			error: () => this._notificationService.snackError(this._i18nSaveFailed()),
		});
	}

	private _create$(document: Invoice | Receipt, edited: { config: unknown }): Observable<RequestResponse<Invoice | Receipt>> {
		const partial = { id: document.id, beneficiaryId: this.beneficiaryId(), config: edited.config };
		return this.kind() === 'invoice'
			? this._invoices.createInvoice$(partial as Partial<Invoice>)
			: this._receipts.createReceipt$(partial as Partial<Receipt>);
	}

	private _patch$(document: Invoice | Receipt, edited: { config: unknown }): Observable<RequestResponse<Invoice | Receipt>> {
		return this.kind() === 'invoice'
			? this._invoices.patchInvoice$(document.id, { config: edited.config as InvoiceConfig })
			: this._receipts.patchReceipt$(document.id, { config: edited.config as ReceiptConfig });
	}

	/** Opens the rendered document in a new tab. A draft arrives watermarked. */
	preview() {
		const document = this.document();
		this.isBusy.set(true);
		this.complianceFailures.set([]);
		const proposed = { ...document, ...this._edited() };
		const asked =
			this.kind() === 'invoice'
				? this._invoices.previewInvoicePdf$(proposed as Partial<Invoice>)
				: this._receipts.previewReceiptPdf$(proposed as Partial<Receipt>);
		asked.pipe(finalize(() => this.isBusy.set(false))).subscribe({
			next: (response) => {
				const blob = response.body;
				if (!blob) {
					this._notificationService.snackError(this._i18nPreviewFailed());
					return;
				}
				window.open(window.URL.createObjectURL(blob), '_blank');
				const missing = response.headers.get(FACTURX_MISSING_HEADER);
				if (missing) this._notificationService.snackWarning(`${this._i18nPreviewWithoutXml()} ${missing}`);
			},
			error: () => this._notificationService.snackError(this._i18nPreviewFailed()),
		});
	}

	check() {
		const external = this.useExternalValidator();
		this.isChecking.set(true);
		this.complianceFailures.set([]);
		const asked: Observable<RequestResponse<ValidationReport>> = this._check$({ external, edited: this._edited() });
		asked.pipe(finalize(() => this.isChecking.set(false))).subscribe({
			next: (response) => {
				// The description is the useful half — "the buyer's country is required" — and it is
				// what tells the admin what to type.
				if (response.error) this._notificationService.snackError(this._reasonOr(response.error, this._i18nCheckFailed()));
				this._readReport(response);
			},
			error: () => this._notificationService.snackError(this._i18nCheckFailed()),
		});
	}

	delete() {
		const document = this.document();
		this._notificationService
			.confirm(`${document.number} — ${this._i18nDeleteExplanation()}`, this._i18nConfirmDeleteTitle(), { confirmButtonText: this._i18nDelete() })
			.closed.subscribe((confirmed) => {
			if (!confirmed) return;
			this.isBusy.set(true);
			const asked: Observable<RequestResponse<{ deleted: boolean }>> =
				this.kind() === 'invoice' ? this._invoices.deleteInvoice$(document.id) : this._receipts.deleteReceipt$(document.id);
			asked.pipe(finalize(() => this.isBusy.set(false))).subscribe((response) => {
				if (response.error) {
					this._notificationService.snackError(response.error.title || this._i18nDeleteFailed());
					return;
				}
				this._notificationService.snackSuccess(this._i18nDeleted());
				this.deleted.emit(document);
			});
		});
	}

	/** The only irreversible action here: the number is taken, the file frozen, the customer told. */
	issue() {
		const document = this.document();
		this._notificationService
			.confirm(this._i18nIssueExplanation(), this._i18nConfirmIssueTitle(), { confirmButtonText: this._i18nIssue() })
			.closed.subscribe((confirmed) => {
				if (!confirmed) return;
				this.isBusy.set(true);
				this.complianceFailures.set([]);
				const asked: Observable<RequestResponse<Invoice | Receipt>> =
					this.kind() === 'invoice' ? this._invoices.issueInvoice$(document.id) : this._receipts.issueReceipt$(document.id);
				asked.pipe(finalize(() => this.isBusy.set(false))).subscribe({
					next: (response) => {
						if (response.error) {
							this._handleIssueError(response.error);
							return;
						}
						if (response.result) this.issued.emit(response.result);
					},
					error: () => this._notificationService.snackError(this._i18nIssueFailed()),
				});
			});
	}

	private _handleIssueError(error: { code?: string; title?: string; description?: string }) {
		if (error.code === 'number_collision') {
			// Nothing was allocated. The draft has already been redrawn onto the free number, so the
			// second attempt succeeds — say so rather than leaving them to guess.
			this._notificationService.snackWarning(`${error.title ?? ''} ${this._i18nRedrawn()}`);
			this.issued.emit(this.document());
			return;
		}
		if (error.code === 'document_not_compliant' && error.description) {
			// The schematron speaks in rule numbers. Showing them verbatim is still the most useful
			// thing: each line names the rule and what it wanted.
			this.complianceFailures.set(error.description.split('\n').filter((line) => line.trim()));
			return;
		}
		this._notificationService.snackError(error.title || this._i18nIssueFailed());
	}

	private _reasonOr(error: { title?: string; description?: string } | undefined, fallback: string) {
		return [error?.title, error?.description].filter(Boolean).join(' — ') || fallback;
	}
}
